import { Owner } from "solid-js";
import { runWithOwner } from "solid-js";
import Cookies from "js-cookie";

import CreditLine from "@repo/kikoff-models/lending/CreditLine";
import Subscription from "@repo/kikoff-models/Subscription";
import Transaction from "@repo/kikoff-models/Transaction";
import Analytics from "@repo/utils/Analytics";
import UObject from "@repo/utils/UObject";
import UProxy from "@repo/utils/UProxy";
import UString from "@repo/utils/UString";
import Platform from "@repo/utils-client/Platform";
import AppRouter from "@repo/utils-solid/AppRouter";

import { appApi } from "~/rest.js";

import events, {
  actions,
  EventsSchema,
  FlattenEvents,
  formatted,
} from "./events.js";

export type AnalyticsEvents = {
  conversion: {
    Signup: {};
    "Credit Account Open": {
      tier: Subscription.Tier;
      accountId: CreditLine.Id;
    };
    "Make Credit Line Payment": {
      tier: Subscription.Tier;
      accountId: CreditLine.Id;
      transactionId: Transaction.Id;
    };
    // Only snapchat uses login event
    SnapchatOnlyLogin: {};
  };
  analytics: FlattenEvents<typeof events>;
  other: {
    // Not meant to be populated
  };
};

const base = new Analytics<
  AnalyticsEvents,
  {
    owner: Owner;
  }
>();

export declare namespace Registration {
  type Analytics = Analytics.Client.Registration.Sync<typeof base, "analytics">;
  type Conversion = Analytics.Client.Registration.Sync<
    typeof base,
    "conversion"
  >;
  type Other = Analytics.Client.Registration.Sync<typeof base, "other">;
}

const clients =
  // prettier-ignore
  import.meta.glob("./clients/*/*.ts", {
  eager: true,
  import: "default",
}) as {
/* GENERATED */ "./clients/analytics/amplitude.ts": typeof import("./clients/analytics/amplitude.ts")["default"],
/* GENERATED */ "./clients/analytics/clarity.ts": typeof import("./clients/analytics/clarity.ts")["default"],
/* GENERATED */ "./clients/conversion/facebook.ts": typeof import("./clients/conversion/facebook.ts")["default"],
/* GENERATED */ "./clients/conversion/googleAnalytics.ts": typeof import("./clients/conversion/googleAnalytics.ts")["default"],
/* GENERATED */ "./clients/conversion/googleTagManager.ts": typeof import("./clients/conversion/googleTagManager.ts")["default"],
/* GENERATED */ "./clients/conversion/impactRadius.ts": typeof import("./clients/conversion/impactRadius.ts")["default"],
/* GENERATED */ "./clients/conversion/podscribe.ts": typeof import("./clients/conversion/podscribe.ts")["default"],
/* GENERATED */ "./clients/conversion/snapchat.ts": typeof import("./clients/conversion/snapchat.ts")["default"],
/* GENERATED */ "./clients/conversion/tiktok.ts": typeof import("./clients/conversion/tiktok.ts")["default"],
/* GENERATED */ "./clients/other/sentry.ts": typeof import("./clients/other/sentry.ts")["default"]
};

const analytics = !Platform.isClient
  ? (base as never)
  : base
      .authorize(async () => {
        if (import.meta.env.MOBILE) return true;

        if (Cookies.get("consentCookie")) {
          return Cookies.get("consentCookie") === "optIn";
        }

        // only allow analytics if user has opted in or is not in Quebec
        return (await appApi.region.GET({})).region !== "QC";
      })
      .setBaseProperties(() => ({
        solidjs: true,
        path: (() => {
          // We cannot always guarantee analytics.track won't be called before
          // AppRouter is registered. If it is AppRouter will throw, fall back
          // to window path
          try {
            return AppRouter.pathname;
          } catch (e) {
            return window.location.pathname;
          }
        })(),
        "user agent": window.navigator.userAgent,
      }))
      .addClients(
        (() => {
          const res = UObject.trimKeys(clients, "./clients/", ".ts");
          for (const [key, client] of Object.entries(res)) {
            res[key] = () =>
              // @ts-expect-error
              Promise.resolve(client()).then((client) => {
                if (!client) return client;
                return UObject.transform(client, ([key, value]) => [
                  key,
                  value &&
                    ((...args: any[]) =>
                      runWithOwner(analytics.context.owner, () =>
                        value(...args)
                      )),
                ]);
              });
          }
          return res;
        })()
      );

export default analytics;

type Track<E extends EventsSchema> = {
  [K in keyof E]: (E[K] extends {
    readonly [actions]: infer Actions;
  }
    ? <Action extends keyof Actions>(
        action: Action,
        ...[properties]: keyof Actions[Action] extends never
          ? [(undefined | {})?]
          : [Actions[Action]]
      ) => void
    : {}) &
    Track<Omit<E[K], symbol>>;
};

export const track = UProxy.createPathProxy((path) => (action, properties) => {
  let current = events;
  analytics.track(
    // @ts-expect-error
    `${path.map((segment) => (current = current[segment])[formatted]).join(" - ")}: ${UString.capitalize(action)}`,
    properties
  );
}) as unknown as Track<typeof events>;
